/**
 * Side panel that renders filesystem files and task details alongside the chat.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useThread } from '@assistant-ui/react';
import {
  XIcon,
  FileTextIcon,
  FolderIcon,
  DownloadIcon,
  ExternalLinkIcon,
  ListTodoIcon,
  CheckIcon,
  BanIcon,
  ClockIcon,
  LoaderIcon,
} from 'lucide-react';
import { useExternalAssets } from '@/client/lib/assetsContext';
import { useFiles, type FileData } from '@/client/lib/filesContext';
import { useTasks, type Task } from '@/client/lib/tasksContext';
import { useTaskDecisions } from '@/client/components/chat/TaskReview';
import { Button } from '@/client/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { cn } from '@/client/lib/utils';
import { injectLinkHandler } from '@/client/components/asset-renderers';
import { MarkdownEditor } from '@/client/components/chat/MarkdownEditor';

/* ─── Filesystem File Editor ──────────────────────────────────────── */

function getFileExtBadge(path: string): string {
  return path.split('.').pop()?.toUpperCase() || 'FILE';
}

function getFileName(path: string): string {
  const name = path.split('/').pop() ?? path;
  const withoutExt = name.replace(/\.[^.]+$/, '');
  return withoutExt
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isMarkdownFile(path: string): boolean {
  return path.endsWith('.md') || path.endsWith('.markdown');
}

function isHtmlFile(path: string): boolean {
  return path.endsWith('.html') || path.endsWith('.htm');
}

/**
 * HTML file viewer — renders content in an iframe using a blob URL.
 *
 * Uses blob: URL instead of srcDoc to match how external asset iframes
 * (GCS) load via real URLs. This avoids srcDoc-specific layout
 * behaviors that can interfere with the thread viewport's scroll detection.
 * The blob URL is only recreated when content actually changes.
 */
const HtmlFileViewer: FC<{ path: string; content: string; isStreaming: boolean }> = ({ path, content, isStreaming }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const mountedContentRef = useRef<string | null>(null);

  // Create blob URL on mount and whenever content changes after streaming stops
  useEffect(() => {
    // Always render on first mount regardless of streaming state
    const isFirstRender = mountedContentRef.current === null;
    if (!isFirstRender && isStreaming) return;

    // Skip if content hasn't changed
    if (content === mountedContentRef.current) return;
    mountedContentRef.current = content;

    const html = injectLinkHandler(content);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  }, [content, isStreaming]);

  // Re-render on path change (switching files)
  useEffect(() => {
    mountedContentRef.current = null;
  }, [path]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  if (!blobUrl) return null;

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
      <iframe
        src={blobUrl}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        className="flex-1 border-0"
        title={getFileName(path)}
      />
    </div>
  );
};

const FileEditor: FC<{ path: string; data: FileData; isStreaming: boolean }> = ({ path, data, isStreaming }) => {
  const { updateFile } = useFiles();
  const content = data.content;

  const handleChange = useCallback((newContent: string) => {
    updateFile(path, newContent);
  }, [path, updateFile]);

  if (isHtmlFile(path)) {
    return <HtmlFileViewer path={path} content={content} isStreaming={isStreaming} />;
  }

  if (isMarkdownFile(path)) {
    return (
      <div className="asset-panel-editor flex flex-1 flex-col min-h-0 overflow-hidden">
        <MarkdownEditor content={content} isStreaming={isStreaming} onContentChange={handleChange} />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
      <textarea
        className="flex-1 resize-none overflow-y-auto bg-background p-4 font-mono text-xs leading-relaxed outline-none"
        value={content}
        onChange={(e) => handleChange(e.target.value)}
        readOnly={isStreaming}
        spellCheck={false}
      />
    </div>
  );
};

/* ─── Task Status Icon ───────────────────────────────────────────── */

function TaskStatusIcon({ status }: { status: Task['status'] }) {
  if (status === 'completed') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
        <CheckIcon className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <XIcon className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-destructive bg-background">
        <BanIcon className="size-2.5 text-destructive" />
      </span>
    );
  }
  if (status === 'approved') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-primary bg-background">
        <LoaderIcon className="size-2.5 animate-spin text-primary" />
      </span>
    );
  }
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-warning bg-background">
      <ClockIcon className="size-2.5 text-warning" />
    </span>
  );
}

/* ─── Task List + Detail (master-detail in sidebar) ──────────────── */

function TaskListView({ tasks, initialSelectedId }: { tasks: Task[]; initialSelectedId?: string | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);

  // Update selection when opened from chat View button
  useEffect(() => {
    if (initialSelectedId) {
      setSelectedId(initialSelectedId);
    }
  }, [initialSelectedId]);
  const selectedTask = selectedId ? tasks.find((t) => t.id === selectedId) : null;

  if (tasks.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
        <ListTodoIcon className="size-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No tasks yet</p>
        <p className="text-xs text-muted-foreground/60">Tasks will appear here when Gaia proposes changes for your review.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Task list (scrollable, max ~6 rows visible) */}
      <div className="shrink-0 overflow-y-auto border-b" style={{ maxHeight: '216px' }}>
        {tasks.map((task) => (
          <button
            key={task.id}
            onClick={() => setSelectedId(selectedId === task.id ? null : task.id)}
            className={cn(
              'flex w-full cursor-pointer items-center gap-3 border-b last:border-b-0 px-3 py-2 text-left transition-colors',
              selectedId === task.id ? 'bg-primary/5' : 'hover:bg-muted/50',
            )}
          >
            <TaskStatusIcon status={task.status} />
            <p className={cn(
              'min-w-0 flex-1 truncate text-xs font-medium',
              task.status === 'rejected' && 'line-through text-muted-foreground',
            )}>
              {task.title}
            </p>
            <span
              className={cn(
                'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                task.status === 'completed' && 'bg-emerald-50 text-emerald-600',
                task.status === 'failed' && 'bg-red-50 text-red-600',
                task.status === 'rejected' && 'bg-red-50 text-red-600',
                task.status === 'approved' && 'bg-violet-50 text-violet-600',
                task.status === 'pending' && 'bg-amber-50 text-amber-600',
              )}
            >
              {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
            </span>
          </button>
        ))}
      </div>

      {/* Detail below the list */}
      {selectedTask ? (
        <TaskDetail task={selectedTask} />
      ) : (
        <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
          Select a task to view details
        </div>
      )}
    </div>
  );
}

/* ─── Task Detail (with decision toggles) ────────────────────────── */

function TaskDetail({ task: selectedTask }: { task: Task }) {
  const { decisions, setDecision, isReviewMode } = useTaskDecisions();
  const decision = decisions.get(selectedTask.id);

  const activeStyles = {
    approved: 'bg-emerald-50 border-emerald-400 text-emerald-600',
    pending: 'bg-amber-50 border-amber-400 text-amber-600',
    rejected: 'bg-red-50 border-red-400 text-red-600',
  };

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <h3 className="text-sm font-semibold">{selectedTask.title}</h3>

      {/* Decision toggles — synced with conversation */}
      {isReviewMode && selectedTask.status === 'pending' && (
        <div className="mt-3 flex items-center gap-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setDecision(selectedTask.id, decision === 'approved' ? 'pending' : 'approved')}
                className={cn(
                  'flex size-7 cursor-pointer items-center justify-center rounded-md border-[1.5px] transition-all duration-150',
                  decision === 'approved'
                    ? activeStyles.approved
                    : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/50',
                )}
              >
                <CheckIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">Approve</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setDecision(selectedTask.id, 'pending')}
                className={cn(
                  'flex size-7 cursor-pointer items-center justify-center rounded-md border-[1.5px] transition-all duration-150',
                  decision === 'pending' || !decision
                    ? activeStyles.pending
                    : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/50',
                )}
              >
                <ClockIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">Awaiting</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setDecision(selectedTask.id, decision === 'rejected' ? 'pending' : 'rejected')}
                className={cn(
                  'flex size-7 cursor-pointer items-center justify-center rounded-md border-[1.5px] transition-all duration-150',
                  decision === 'rejected'
                    ? activeStyles.rejected
                    : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/50',
                )}
              >
                <BanIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">Reject</TooltipContent>
          </Tooltip>
        </div>
      )}

      {selectedTask.description && (
        <p className="mt-3 text-xs leading-relaxed text-foreground">{selectedTask.description}</p>
      )}

      {(selectedTask.reasoning || selectedTask.impact) && (
        <div className="mt-3 space-y-2">
          {selectedTask.reasoning && (
            <div className="rounded-lg bg-muted/50 px-3 py-2.5">
              <p className="mb-1 text-[11px] font-semibold text-muted-foreground">Why</p>
              <p className="text-xs leading-relaxed text-foreground">{selectedTask.reasoning}</p>
            </div>
          )}
          {selectedTask.impact && (
            <div className="rounded-lg bg-primary/[0.04] px-3 py-2.5">
              <p className="mb-1 text-[11px] font-semibold text-primary">Expected impact</p>
              <p className="text-xs leading-relaxed text-foreground">{selectedTask.impact}</p>
            </div>
          )}
        </div>
      )}

      {selectedTask.url && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              window.parent.postMessage({ type: 'GAIA_EMBED_NAVIGATE', url: selectedTask.url }, '*');
            }}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-primary hover:bg-primary/5"
          >
            <ExternalLinkIcon className="size-3" />
            Open in helpdesk
          </button>
        </div>
      )}

        </div>
  );
}

/* ─── Asset Panel ────────────────────────────────────────────────── */

export const AssetPanel: FC = () => {
  const { setPanelCollapsed, panelTab, setPanelTab } = useExternalAssets();
  const { tasks } = useTasks();
  const { files, selectedFile, setSelectedFile } = useFiles();
  const isStreaming = useThread((t) => t.isRunning);

  const visibleFiles = useMemo(
    () => Object.entries(files).filter(([path]) => path.startsWith('/shared/')),
    [files],
  );

  // Auto-select first file only if no file is selected or selection doesn't exist
  const selectedExists = selectedFile && visibleFiles.some(([p]) => p === selectedFile);
  useEffect(() => {
    if (visibleFiles.length > 0 && !selectedExists) {
      setSelectedFile(visibleFiles[0][0]);
    }
  }, [visibleFiles, selectedExists, setSelectedFile]);

  const handleClose = () => {
    setPanelCollapsed(true);
  };

  const activeFile = visibleFiles.find(([path]) => path === selectedFile);

  const handleDownload = (filePath: string) => {
    const file = visibleFiles.find(([p]) => p === filePath);
    if (!file) return;
    const [, data] = file;
    const content = data.content;
    const rawName = filePath.split('/').pop() ?? 'file.txt';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = rawName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Determine active tab — task: prefix goes to tasks tab
  const activeTab = panelTab === 'tasks' || panelTab?.startsWith('task:') ? 'tasks' : 'files';

  // Auto-select task from panelTab (when opened from chat View button)
  const initialTaskId = panelTab?.startsWith('task:') ? panelTab.slice(5) : null;

  return (
    <div className="flex h-full flex-col">
      {/* Header with tabs */}
      <div className="flex items-center border-b px-2 py-1.5">
        <div className="flex min-w-0 flex-1 gap-1">
          <button
            onClick={() => setPanelTab('tasks')}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors',
              activeTab === 'tasks' ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            <ListTodoIcon className="size-3" />
            Tasks
            {tasks.length > 0 && (
              <span className="text-[10px] text-muted-foreground">{tasks.length}</span>
            )}
          </button>
          <button
            onClick={() => setPanelTab('files')}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors',
              activeTab === 'files' ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            <FileTextIcon className="size-3" />
            Files
            {visibleFiles.length > 0 && (
              <span className="text-[10px] text-muted-foreground">{visibleFiles.length}</span>
            )}
          </button>
        </div>

        <Button variant="ghost" size="icon" className="size-6 shrink-0" onClick={handleClose}>
          <XIcon className="size-3.5" />
        </Button>
      </div>

      {/* Files tab — master-detail: list at top, preview below */}
      {activeTab === 'files' && (
        <div className="flex min-h-0 flex-1 flex-col">
          {visibleFiles.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
              <FolderIcon className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No files yet</p>
              <p className="text-xs text-muted-foreground/60">Files will appear here as Gaia creates or updates them during the conversation.</p>
            </div>
          ) : (
            <>
              {/* File list (scrollable, max ~6 rows visible) */}
              <div className="shrink-0 overflow-y-auto border-b" style={{ maxHeight: '216px' }}>
                {visibleFiles.map(([path]) => (
                  <button
                    key={path}
                    onClick={() => setSelectedFile(path)}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-3 border-b last:border-b-0 px-3 py-2 text-left transition-colors',
                      path === selectedFile ? 'bg-primary/5' : 'hover:bg-muted/50',
                    )}
                  >
                    <FileTextIcon className="size-3 shrink-0 text-muted-foreground" />
                    <p className="min-w-0 flex-1 truncate text-xs font-medium">{getFileName(path)}</p>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {getFileExtBadge(path)}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDownload(path); }}
                      className="shrink-0 cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Download"
                    >
                      <DownloadIcon className="size-3" />
                    </button>
                  </button>
                ))}
              </div>

              {/* File preview below */}
              {activeFile ? (
                <FileEditor path={activeFile[0]} data={activeFile[1]} isStreaming={isStreaming} />
              ) : (
                <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
                  Select a file to preview
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tasks tab */}
      {activeTab === 'tasks' && (
        <TaskListView tasks={tasks} initialSelectedId={initialTaskId} />
      )}
    </div>
  );
};
